import React, { useMemo, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, CircularProgress, useTheme } from "@mui/material";
import { VM_Colors } from "../Styles/VMStyles";
import { useBrowserZoomFontScale } from "../Hooks/useBrowserZoomFontScale";

/**
 * ReusableDataGrid — responsive wrapper around MUI DataGrid.
 *
 * Note: Never use window.devicePixelRatio as “browser zoom”. On HiDPI/Retina
 * displays (common on Mac), DPR is often 2 at 100% zoom — scaling typography by
 * DPR doubles font sizes incorrectly and clips headers/cells.
 */
const ReusableDataGrid = ({
  rows = [],
  columns = [],
  loading = false,
  page = 0,
  pageSize = 10,
  rowCount = 0,
  paginationMode = "server",
  onPageChange,
  onPageSizeChange,
  getRowId = (row) => row.id,
  height = 600,
  /** When true, grid grows with rows and uses the page scroll instead of an inner vertical scrollbar. */
  autoHeight = false,
  pageSizeOptions = [5, 10, 25, 50],
  rowsPerPageOptions = undefined,
  getRowHeight = undefined,
  /** Fixed row height (px). Ignored when `getRowHeight` is provided or `variableRowHeight` is true. */
  rowHeight = 52,
  /** When true, rows grow with content (legacy behavior). */
  variableRowHeight = false,
  disableColumnMenu = true,
  disableColumnFilter = true,
  disableColumnSelector = true,
  disableDensitySelector = true,
  hideFooterSelectedRowCount = true,
  customSx = {},
  headerBackgroundColor = VM_Colors.primary,
  headerTextColor = "#FFFFFF",
  localeText: localeTextProp = {},
  ...restProps
}) => {
  const theme = useTheme();
  const zoomFontScale = useBrowserZoomFontScale();

  // Handle rowsPerPageOptions as alias for pageSizeOptions
  const finalPageSizeOptions = rowsPerPageOptions || pageSizeOptions;

  const localeText = useMemo(
    () => ({
      paginationRowsPerPage: "Rows per page:",
      ...localeTextProp,
    }),
    [localeTextProp]
  );

  const headerFontSize = `${0.875 * zoomFontScale}rem`;

  const responsiveDataGridStyle = useMemo(
    () => ({
      width: "100%",
      maxWidth: "100%",
      border: `1px solid ${VM_Colors.light_gray}`,
      borderRadius: 1,
      fontFamily: "Lato, Arial, sans-serif",
      fontSize: `${0.875 * zoomFontScale}rem`,
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",

      "& .MuiDataGrid-root": {
        width: "100%",
        maxWidth: "100%",
      },
      "& .MuiDataGrid-main": {
        width: "100%",
        maxWidth: "100%",
        overflow: autoHeight ? "visible" : "hidden",
      },

      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: headerBackgroundColor,
        borderBottomColor: VM_Colors.light_gray,
        minHeight: { xs: 40, sm: 44 },
        fontSize: headerFontSize,
      },
      "& .MuiDataGrid-columnHeader": {
        backgroundColor: headerBackgroundColor,
        color: headerTextColor,
        borderRight: "1px solid rgba(255, 255, 255, 0.22)",
        py: { xs: 0.5, sm: 0.75 },
        px: { xs: 0.75, sm: 1 },
        fontSize: headerFontSize,
        "&:focus, &:focus-within": {
          outlineOffset: "-2px",
        },
      },
      "& .MuiDataGrid-columnHeaderTitleContainer": {
        gap: theme.spacing(0.5),
        minWidth: 0,
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 600,
        lineHeight: 1.25,
        color: headerTextColor,
        fontSize: headerFontSize,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        wordBreak: "normal",
      },
      "& .MuiDataGrid-sortIcon": {
        fontSize: `${1 * zoomFontScale}rem`,
        color: "rgba(255,255,255,0.92)",
      },
      "& .MuiDataGrid-iconSeparator": {
        display: "none",
      },

      "& .MuiDataGrid-cell": {
        py: 0,
        px: { xs: 0.75, sm: 1.25 },
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontSize: `${Math.max(0.8125 * zoomFontScale, 0.7)}rem`,
        lineHeight: 1.25,
        borderColor: VM_Colors.light_gray,
        color: VM_Colors.black,
        "&:focus-within": {
          outline: `2px solid ${VM_Colors.primary}`,
          outlineOffset: "-2px",
        },
        /** Let flex children shrink so long IDs don’t overlap adjacent columns */
        "& > *": {
          minWidth: 0,
          maxWidth: "100%",
        },
      },
      "& .MuiDataGrid-cell--textRight": {
        justifyContent: "flex-end !important",
        textAlign: "right !important",
      },
      "& .MuiDataGrid-cell--textCenter": {
        justifyContent: "center !important",
        textAlign: "center !important",
      },
      "& .MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-columnHeaderTitleContainer":
        {
          justifyContent: "flex-start !important",
        },
      "& .MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-menuIcon": {
        marginLeft: "0 !important",
      },
      "& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer":
        {
          flexDirection: "row !important",
          justifyContent: "flex-start !important",
        },
      "& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-menuIcon": {
        marginRight: "0 !important",
        marginLeft: "0 !important",
      },

      "& .MuiDataGrid-row": {
        "&:hover": {
          backgroundColor: "rgba(30, 58, 138, 0.05)",
        },
        "&.Mui-selected": {
          backgroundColor: "rgba(30, 58, 138, 0.1)",
          "&:hover": {
            backgroundColor: "rgba(30, 58, 138, 0.15)",
          },
        },
      },

      "& .MuiDataGrid-footerContainer": {
        backgroundColor: VM_Colors.white2,
        borderTopColor: VM_Colors.light_gray,
        fontSize: `${0.75 * zoomFontScale}rem`,
        flexWrap: "wrap",
      },
      "& .MuiTablePagination-root": {
        fontSize: `${0.75 * zoomFontScale}rem`,
      },
      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
        fontSize: `${0.75 * zoomFontScale}rem`,
      },
      /** DataGrid hides rows-per-page label & select below `sm` — show on all breakpoints */
      "& .MuiTablePagination-selectLabel": {
        display: "block !important",
        marginRight: theme.spacing(1),
      },
      "& .MuiTablePagination-input": {
        display: "inline-flex !important",
      },

      "& .MuiDataGrid-virtualScroller": {
        overflowX: "hidden !important",
        overflowY: autoHeight ? "visible !important" : "auto",
      },
      ...(!autoHeight
        ? {
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
              width: 8,
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
              backgroundColor: VM_Colors.white2,
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
              backgroundColor: VM_Colors.light_gray,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: VM_Colors.steel_gray,
              },
            },
          }
        : {}),

      "& .MuiDataGrid-overlayWrapper": {
        backgroundColor: "rgba(255, 255, 255, 0.8)",
      },

      "@media print": {
        "& .MuiDataGrid-footerContainer": {
          display: "none",
        },
        "& .MuiDataGrid-virtualScroller": {
          overflow: "visible !important",
        },
      },

      ...customSx,
    }),
    [
      theme,
      headerBackgroundColor,
      headerTextColor,
      customSx,
      zoomFontScale,
      autoHeight,
    ]
  );

  const resolvedGetRowHeight = useMemo(() => {
    if (getRowHeight) return getRowHeight;
    if (variableRowHeight) return () => "auto";
    return () => rowHeight;
  }, [getRowHeight, rowHeight, variableRowHeight]);

  const handlePaginationModelChange = useCallback(
    (model) => {
      if (onPageChange && model.page !== page) {
        onPageChange(model.page);
      }
      if (onPageSizeChange && model.pageSize !== pageSize) {
        onPageSizeChange(model.pageSize);
      }
    },
    [onPageChange, onPageSizeChange, page, pageSize]
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: autoHeight ? "auto" : height,
        position: "relative",
        overflowX: "hidden",
        overflowY: autoHeight ? "visible" : "hidden",
      }}
    >
      <DataGrid
        localeText={localeText}
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        loading={loading}
        pagination
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        rowCount={rowCount}
        paginationMode={paginationMode}
        pageSizeOptions={finalPageSizeOptions}
        autoHeight={autoHeight}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableColumnMenu
        hideFooterSelectedRowCount
        disableColumnSorting
        disableSelectionOnClick
        getRowHeight={resolvedGetRowHeight}
        columnHeaderHeight={rowHeight}
        sx={responsiveDataGridStyle}
        slots={{
          loadingOverlay: () => (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: `${0.875 * zoomFontScale}rem`,
                color: VM_Colors.steel_gray,
              }}
            >
              No rows available
            </Box>
          ),
          noResultsOverlay: () => (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: `${0.875 * zoomFontScale}rem`,
                color: VM_Colors.steel_gray,
              }}
            >
              No results found
            </Box>
          ),
        }}
        {...restProps}
      />
    </Box>
  );
};

export default React.memo(ReusableDataGrid);
